import type { NotificationHubTemplateBlockInput } from '@/__generated__/graphql/types';

import {
  METRICA_POR_ID,
  MetricaSinTokenDelContrato,
} from './components/team-metrics/metric-token-map';
import type { TTeamMetricId } from './components/team-metrics/types';
import type {
  TEmailEditorBlock,
  TMetricsBlock,
} from './mail-canvas/blocks/block-types';

export type TResolveMetricLabel = (metricId: TTeamMetricId) => string;

type TContractProps = NotificationHubTemplateBlockInput['props'];

const metricsItemsFor = (
  block: TMetricsBlock,
  resolveMetricLabel: TResolveMetricLabel,
): TContractProps => ({
  items: block.metricIds.map(metricId => {
    const entrada = METRICA_POR_ID.get(metricId);

    if (!entrada) {
      throw new MetricaSinTokenDelContrato(metricId);
    }

    return {
      token: entrada.token,
      label: resolveMetricLabel(metricId),
      display: entrada.display,
    };
  }),
});

/**
 * Props per block type, per the `create_email_template`/`edit_email_template`
 * catalog table — the two mutations share one catalog (`.specs/ANLT-706/
 * contratos-backend/2026-09-08-edit-email-template-mutation.md`: "El plano
 * que viaja y las reglas de validación son idénticos a los de create"). Every
 * mapper that builds a `NotificationHubTemplateBlockInput` derives its props
 * from here, so the three mutations (create, send-test-email, edit) never
 * diverge on what a block sends.
 *
 * `align`/`variant`/`alt`/`width` fall back to the contract's own defaults
 * only when the block does not carry a value — a template hydrated from a
 * block written by another path (seed, API) keeps its own value instead of
 * being reset (ANLT-706, ENCARGO I).
 *
 * Returns `undefined` for an IMAGE block with no uploaded file — the caller
 * decides what that means (skip the block, or fail).
 */
export const blockPropsForContract = (
  block: TEmailEditorBlock,
  resolveMetricLabel: TResolveMetricLabel,
): TContractProps | undefined => {
  if (block.type === 'TEXT') {
    return { html: block.html, align: block.align ?? 'LEFT' };
  }

  if (block.type === 'BUTTON') {
    return {
      text: block.text,
      href: block.link,
      align: block.align ?? 'CENTER',
      variant: block.variant ?? 'PRIMARY',
    };
  }

  if (block.type === 'IMAGE') {
    return block.fileId
      ? {
          file_id: block.fileId,
          alt: block.alt ?? '',
          width: block.width ?? 600,
        }
      : undefined;
  }

  if (block.type === 'DIVIDER') {
    return {};
  }

  return metricsItemsFor(block, resolveMetricLabel);
};
